import { defaults, isEmpty } from 'lodash';
import { Inject, Injectable } from '../../injection';
import { isNil } from '../../utils';

import { API_ENDPOINT_INFORMATION_RECORDS, IApiEndpointInformation } from '../interfaces/api-endpoint-information.interface';
import { ParameterValueType } from '../interfaces/dictionary.interface';
import { MobizServerApi } from '../other/mobiz-server-api.enum';

import { AuthGrantType } from './auth-token-grant';
import { CONFIGURATION_SERVICE, IConfigurationService } from './configuration.interface';
import { IServerApiBuilderService } from './server-api-builder.interface';
import { IStringTemplateService } from './string-template.interface';

@Injectable()
export class MobizServerApiBuilderService implements IServerApiBuilderService {
  public get DEFAULT_API_ENDPOINT_INFORMATION(): Required<IApiEndpointInformation> {
    return {
      noPrefix: false,
      grantType: AuthGrantType.Password,
    };
  }

  constructor(
    @Inject(API_ENDPOINT_INFORMATION_RECORDS) private readonly apiEndpointInformationRecords: Record<string, IApiEndpointInformation>[],
    @Inject(CONFIGURATION_SERVICE) private readonly configuration: IConfigurationService,
    @Inject(IStringTemplateService) private readonly templateService: IStringTemplateService,
  ) {
    this.validateConfigurationValues();
  }

  public getUrl<A extends string = MobizServerApi>(
    serverApi: A,
    urlParameters?: Record<string, ParameterValueType>,
    queryParameters?: Record<string, ParameterValueType>,
  ): string {
    const includeApiVersion = !this.getApiEndpointInformation(serverApi).noPrefix;
    const baseUrl = this.getBaseUrl(serverApi, urlParameters, includeApiVersion);

    const queryString = this.buildParamQueryString(queryParameters);
    let url = baseUrl;

    if (queryString !== '') {
      url += queryString;
    }
    return url;
  }

  public getBaseUrl<A extends string = MobizServerApi>(
    serverApi: A,
    urlParameters?: Record<string, ParameterValueType>,
    includeApiVersion?: boolean,
  ): string {
    let urlTemplate = this.configuration.baseUrl;
    if (includeApiVersion) {
      urlTemplate += `api/${this.configuration.apiVersion}/`;
    }
    urlTemplate += serverApi;

    return this.templateService.compile(urlTemplate, urlParameters);
  }

  public buildParamQueryString(urlParameters?: Record<string, ParameterValueType>): string {
    if (!urlParameters) {
      return '';
    }
    const paramNames: string[] = this.getQueryParametersWithValue(urlParameters);
    if (paramNames.length === 0) {
      return '';
    }
    let queryString = '';
    paramNames.forEach((paramName: string, index: number): void => {
      const param = `${ paramName }=` + encodeURIComponent(`${ urlParameters[paramName] }`);
      queryString += (index === 0 ? '?' : '&') + param;
    });
    return queryString;
  }

  private validateConfigurationValues(): void {
    if (isEmpty(this.configuration.baseUrl)) {
      throw new Error('The "baseUrl" configuration is required.');
    }
    if (this.configuration.baseUrl.substring(this.configuration.baseUrl.length - 1) !== '/') {
      throw new Error(`The "baseUrl" configuration value "${this.configuration.baseUrl}" must end with "/".`);
    }
    if (isEmpty(this.configuration.apiVersion)) {
      throw new Error('The "apiVersion" configuration is required.');
    }
    if (this.configuration.apiVersion.indexOf('/') !== -1) {
      throw new Error(`The "apiVersion" configuration value "${this.configuration.apiVersion}" should not contain any "/" characters.`);
    }
  }

  public getApiEndpointInformation<A extends string = MobizServerApi>(endpoint: A): Required<IApiEndpointInformation> {
    for (const record of this.apiEndpointInformationRecords) {
      if (Object.keys(record).includes(endpoint)) {
        return defaults({}, record[endpoint], this.DEFAULT_API_ENDPOINT_INFORMATION);
      }
    }
    return this.DEFAULT_API_ENDPOINT_INFORMATION;
  }

  private getQueryParametersWithValue(urlParameters: Record<string, ParameterValueType>): string[] {
    let paramKeyList: string[] = Object.keys(urlParameters);
    let filteredParamKeyList: string[] = [];

    paramKeyList.forEach((pKey: string): void => {
      let value: ParameterValueType = urlParameters[pKey];
      if (!isNil(value)
        && !(typeof value === 'string' && value.trim() === '')) {
        filteredParamKeyList.push(pKey);
      }
    });
    return filteredParamKeyList;
  }
}
